export const getDesignIdFromSearch = (search = '') => {
  const params = new URLSearchParams(search);
  return params.get('design_id') || params.get('canvas_id') || null;
};

export const getExplicitDesignId = (source = {}) => {
  if (!source || typeof source !== 'object') {
    return { provided: false, value: null };
  }

  for (const key of ['design_id', 'canvas_id']) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const rawValue = source[key];
      const value =
        typeof rawValue === 'string' && rawValue.trim()
          ? rawValue.trim()
          : null;
      return { provided: true, value };
    }
  }

  return { provided: false, value: null };
};

export const resolveDesignId = (source = {}, search = '') => {
  const explicit = getExplicitDesignId(source);
  return explicit.provided ? explicit.value : getDesignIdFromSearch(search);
};

export const resolveNavigationDesignId = ({
  source = {},
  search = '',
  currentUid = null,
  targetUid = null,
} = {}) => {
  const explicit = getExplicitDesignId(source);
  if (explicit.provided) return explicit.value;

  return String(currentUid || '') === String(targetUid || '')
    ? getDesignIdFromSearch(search)
    : null;
};

export const mergeCommandOptions = (config, params) => ({
  ...(config && typeof config === 'object' && !Array.isArray(config)
    ? config
    : {}),
  ...(params && typeof params === 'object' && !Array.isArray(params)
    ? params
    : {}),
});

export const createLatestMessageListener = (handlerRef) => (event) =>
  handlerRef.current?.(event);

export const isTrustedParentMessage = (
  event,
  parentWindow = globalThis?.parent
) =>
  Boolean(
    event &&
      event.source === parentWindow &&
      event.data &&
      typeof event.data === 'object' &&
      !Array.isArray(event.data)
  );

export const RUN_PRODUCTION_AND_CREATE_PROPOSAL =
  'run_production_and_create_proposal';

export const applyProposalCommandConfig = async (designTool, configForRun) => {
  if (!configForRun || typeof configForRun !== 'object') {
    return;
  }
  if (typeof designTool?.applyProductionSettings === 'function') {
    await designTool.applyProductionSettings({ ...configForRun });
  }
};

export const requireSavedLayoutForProposal = async ({
  designTool,
  getPanelCount,
  waitForPanels,
  commandName,
  afterSegmentsWait = async () => {},
}) => {
  const hasSegments = designTool.handleCurrentAccordion('segments');
  await afterSegmentsWait();
  if (!hasSegments) {
    const error = new Error('0 number of predicted roof segments');
    error.code = '42';
    throw error;
  }

  if (getPanelCount() === 0) {
    try {
      await waitForPanels();
    } catch {
      const error = new Error(
        `No saved panel layout was loaded for this lead. Create or load a panel layout before ${commandName}.`
      );
      error.code = '54';
      throw error;
    }
  }

  if (getPanelCount() === 0) {
    const error = new Error(
      'No panel layout is available to create proposal'
    );
    error.code = '54';
    throw error;
  }
};

export const runProductionAndCreateProposal = async ({
  designTool,
  configForRun,
  getPanelCount,
  waitForPanels,
  runProduction,
  proposalParams,
  loadAfter = true,
  commandName = RUN_PRODUCTION_AND_CREATE_PROPOSAL,
  afterSegmentsWait = async () => {},
}) => {
  try {
    await applyProposalCommandConfig(designTool, configForRun);
  } catch (err) {
    const error = new Error(
      err?.message || 'Invalid production configuration'
    );
    error.code = '51';
    throw error;
  }

  await requireSavedLayoutForProposal({
    designTool,
    getPanelCount,
    waitForPanels,
    commandName,
    afterSegmentsWait,
  });

  try {
    await runProduction(proposalParams);
  } catch (err) {
    const error = new Error(
      err?.message || 'Solar production calculation failed'
    );
    error.code = '60';
    throw error;
  }

  try {
    await designTool.handleProposal(proposalParams, loadAfter);
  } catch (err) {
    const error = new Error('Error in creating proposal');
    error.code = '53';
    throw error;
  }
};

export const runPanelPlacementCommand = async (
  designer,
  params,
  onProductionSettingsError = null
) => {
  if (params?.solar_panel) {
    await designer.changePanelHardware(params.solar_panel);
  }

  if (
    typeof params?.onlySelectedRoof === 'boolean' &&
    typeof designer.setPanelPlacementOnlySelectedRoof === 'function'
  ) {
    designer.setPanelPlacementOnlySelectedRoof(params.onlySelectedRoof);
  }

  if (params && typeof designer.applyProductionSettings === 'function') {
    try {
      await designer.applyProductionSettings(params);
    } catch (error) {
      onProductionSettingsError?.(error);
    }
  }

  const result = await designer.handlePlacement(params);
  return result;
};
